import React, { useState, useEffect, useCallback } from 'react';
import { usePayment } from '../../context/PaymentContext';
import BasePaymentWidget from './widgets/BasePaymentWidget';
import CloudPaymentsWidget from './widgets/CloudPaymentsWidget';

// Карта соответствия провайдеров и их виджетов
const widgetMap = {
    'cloud_payments': CloudPaymentsWidget,
    // 'tinkoff': TinkoffWidget,  // добавим позже
    // 'yookassa': YooKassaWidget, // добавим позже
    // 'mock': BasePaymentWidget    // мок использует базовый виджет
};

const PaymentProcessor = ({
                              order,
                              user,
                              onSuccess,
                              onError,
                              onCancel,
                              onRetry,
                              currentStatus,
                              errorMessage,
                              testScenario
                          }) => {
    const {
        providerInstance,
        provider,
        status: contextStatus,
        error: contextError,
        initPayment,
        resetPayment,
        getProviderInfo,
        isProcessing,
        isSuccess,
        isFailed
    } = usePayment();

    const [localProcessing, setLocalProcessing] = useState(false);
    const [paymentResult, setPaymentResult] = useState(null);

    const providerInfo = getProviderInfo();
    const testMode = providerInfo?.testMode || false;

    // Определяем, какой виджет использовать
    const PaymentWidget = widgetMap[provider] || BasePaymentWidget;

    // Используем статус из пропсов, если он есть, иначе из контекста
    const effectiveStatus = currentStatus || contextStatus;
    const effectiveError = errorMessage || contextError;

    /**
     * Обработка нажатия на кнопку оплаты
     * Сначала вызываем initPayment на бэкенде, затем выполняем платеж через провайдера
     * @param {Object} widgetData - данные из виджета (метод оплаты и т.д.)
     */
    const handlePayment = useCallback(async (widgetData = {}) => {
        if (localProcessing) return;

        setLocalProcessing(true);

        try {
            console.log('[PaymentProcessor] Starting payment flow...');

            // Шаг 1: Инициализация платежа на бэкенде
            const orderWithMethod = widgetData.method
                ? { ...order, paymentMethod: widgetData.method }
                : order;
            const initResult = await initPayment(orderWithMethod, user);

            console.log('[PaymentProcessor] Init result:', initResult);

            // Если требуется редирект на чекаут (например, для некоторых провайдеров)
            if (initResult.redirectRequired && initResult.checkoutUrl) {
                window.location.href = initResult.checkoutUrl;
                return;
            }

            // Шаг 2: Если провайдер имеет метод executePayment (CloudPayments),
            // выполняем реальный платеж через виджет
            if (providerInstance && typeof providerInstance.executePayment === 'function') {
                console.log('[PaymentProcessor] Executing payment via provider...');

                const paymentData = {
                    orderId: order.id,
                    amount: order.amount,
                    description: `Заказ #${order.id}`,
                    userId: user?.id,
                    userEmail: user?.email,
                    userPhone: user?.phone,
                    items: order.items,
                    restaurantName: order.restaurantName
                };

                const executeResult = await providerInstance.executePayment(paymentData);

                console.log('[PaymentProcessor] Execute result:', executeResult);

                if (executeResult.success) {
                    try {
                        await onSuccess?.(executeResult);
                        setPaymentResult(executeResult);
                    } catch (confirmError) {
                        // confirmation error already handled by parent via handlePaymentError
                    }
                } else {
                    throw new Error(executeResult.error || 'Payment execution failed');
                }
            } else {
                // Для провайдеров без executePayment (например, наличные)
                onSuccess?.(initResult);
            }
        } catch (error) {
            console.error('[PaymentProcessor] Payment error:', error);
            onError?.(error);
        } finally {
            setLocalProcessing(false);
        }
    }, [order, user, initPayment, providerInstance, onSuccess, onError, localProcessing]);

    /**
     * Обработка повторной попытки
     */
    const handleRetry = useCallback(() => {
        setLocalProcessing(false);
        setPaymentResult(null);
        resetPayment();
        if (onRetry) {
            onRetry();
        }
    }, [resetPayment, onRetry]);

    /**
     * Обработка отмены
     */
    const handleCancel = useCallback(() => {
        setLocalProcessing(false);
        resetPayment();
        if (onCancel) {
            onCancel();
        }
    }, [resetPayment, onCancel]);

    /**
     * Маппинг статусов для виджета
     */
    const getWidgetStatus = useCallback(() => {
        // Если платеж уже успешен
        if (paymentResult?.success || isSuccess) {
            return 'success';
        }

        // Если в процессе выполнения через провайдера
        if (localProcessing || isProcessing) {
            return 'processing';
        }

        // Если ошибка
        if (effectiveError || isFailed) {
            return 'error';
        }

        // По умолчанию готов к оплате
        return 'ready';
    }, [paymentResult, isSuccess, localProcessing, isProcessing, effectiveError, isFailed]);

    /**
     * Проверка на 3D-Secure для CloudPayments
     */
    const getFinalStatus = useCallback(() => {
        const widgetStatus = getWidgetStatus();

        // Для CloudPayments и 3D-Secure
        if (provider === 'cloud_payments' &&
            widgetStatus === 'processing' &&
            effectiveError?.code === '3DSecureRequired') {
            return '3dsecure';
        }

        return widgetStatus;
    }, [provider, getWidgetStatus, effectiveError]);

    const widgetStatus = getFinalStatus();

    // Если нет заказа, не рендерим ничего
    if (!order) {
        return null;
    }

    // Логи для отладки
    console.log('[PaymentProcessor] Render:', {
        provider,
        widgetStatus,
        effectiveStatus,
        testMode,
        hasProviderInstance: !!providerInstance,
        localProcessing,
        paymentResult
    });

    return (
        <PaymentWidget
            amount={order.amount}
            orderId={order.id}
            onPayment={handlePayment}
            onRetry={handleRetry}
            onCancel={handleCancel}
            status={widgetStatus}
            error={effectiveError}
            testMode={testMode}
            provider={providerInstance}
        />
    );
};

export default PaymentProcessor;